import React, { useState } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import {
  TextField,
  Button,
  Grid,
  Container,
  IconButton,
  Typography,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DynamicUploadModal from "../../../components/UploadModal";
import { v4 as uuidv4 } from "uuid";
import SymptomSelector from "../../../components/topicCategorySelector/SymptomSelector";

const AddSpecialities = () => {
  const [openModal, setOpenModal] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(""); // State to store the uploaded URL
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [specialities, setSpecialities] = useState({
    description: "",
    id: "",
    imageUrl: "",
    name: "",
    status: "live",
    symptoms: [], // Array of symptom objects
    symptom_ids: [], // Array of symptom IDs
  });

  // Function to handle the modal close
  const handleModalClose = () => {
    setOpenModal(false);
  };

  // Function to handle image upload success
  const handleUploadSuccess = (url) => {
    setUploadedImageUrl(url); // Set the uploaded URL to the state
    setSpecialities((prevData) => ({
      ...prevData,
      imageUrl: url, // Set the uploaded URL in specialities
    }));
  };

  // Function to handle field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSpecialities((prevSpecialities) => ({
      ...prevSpecialities,
      [name]: value,
    }));
  };

  // Handle multiple symptom selection
  const handleSelectSymptom = (selectedSymptom) => {
    setSpecialities((prevData) => {
      const existingIds = prevData.symptom_ids; // Get existing IDs

      if (!existingIds.includes(selectedSymptom.id)) {
        return {
          ...prevData,
          symptoms: [...prevData.symptoms, selectedSymptom], // Add full symptom object
          symptom_ids: [...prevData.symptom_ids, selectedSymptom.id], // Add symptom ID
        };
      }

      return prevData; // No changes if already selected
    });
  };

  // Handle removing a symptom
  const handleRemoveSymptom = (id) => {
    setSpecialities((prevData) => ({
      ...prevData,
      symptoms: prevData.symptoms.filter((symptom) => symptom.id !== id), // Remove full object
      symptom_ids: prevData.symptom_ids.filter((symptomId) => symptomId !== id), // Remove ID
    }));
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const uniqueId = uuidv4();
    const specialitiesData = {
      ...specialities,
      id: uniqueId,
      timestamp: serverTimestamp(),
    };

    try {
      await setDoc(doc(db, "specialities", uniqueId), specialitiesData);
      setOpenSnackbar(true);
      setSpecialities({
        description: "",
        id: "",
        imageUrl: "",
        name: "",
        status: "live",
        symptoms: [],
      });
    } catch (error) {
      console.error("Error adding specialities: ", error);
      alert("Error adding specialities");
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Card>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom>
            Add New Specialities
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={specialities.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={specialities.description}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL"
                  name="imageUrl"
                  value={uploadedImageUrl}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <IconButton
                  onClick={() => setOpenModal(true)}
                  color="primary"
                  aria-label="upload image"
                >
                  <PhotoCamera />
                </IconButton>
              </Grid>
              <Grid item xs={12}>
                <SymptomSelector onSelectSymptom={handleSelectSymptom} />

                <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
                  Selected Symptoms:
                </Typography>
                <Grid container spacing={1}>
                  {specialities.symptoms.map((symptom) => (
                    <Grid item key={symptom.id}>
                      <Chip
                        label={symptom.name}
                        onDelete={() => handleRemoveSymptom(symptom.id)}
                        color="primary"
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                >
                  Add Specialities
                </Button>
              </Grid>
            </Grid>
          </form>
          <DynamicUploadModal
            open={openModal}
            onClose={handleModalClose}
            uploadPreset="bookMyDr"
            folderName="Madecare"
            maxSize={5 * 1024 * 1024}
            onUploadSuccess={handleUploadSuccess}
          />
        </CardContent>
      </Card>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Specialities added successfully!
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddSpecialities;
